import { FormattedMessage } from 'react-intl';

import config from '@/config';
import type { MessageKey } from '@/modules/localizations/translations/types';
import { IconType } from '@/modules/ui';
import { hasWritePermission } from '@/utilities/permission';

import { ListItemType } from '../../constants';
import type { SecurityAuditReportItem } from '../../types';
import { isSecurityAuditAgeSecret, isSecurityAuditDuplicitiesSecret, isSecurityAuditLeaksSecret } from '../../utilities';
import { SecurityAuditGroupItem } from '../SecurityAuditGroupItem';
import { SecurityAuditItemRenderer, type SecurityAuditItemRendererProps } from '../SecurityAuditItemRenderer';
import { iconTypeBySecurityLevel } from '../SecurityAuditLevelItemRenderer';
import { SecurityAuditSecretItem } from '../SecurityAuditSecretItem';
import { getGroupMessageId } from './utils';

export interface SecurityAuditAgeItemRendererProps extends Omit<SecurityAuditItemRendererProps<SecurityAuditReportItem>, 'children'> {}

export const SecurityAuditReportItemRenderer = (props: SecurityAuditAgeItemRendererProps) => (
    <SecurityAuditItemRenderer<SecurityAuditReportItem> {...props}>
        {({ item, isFirstGroup, isLastInGroup }) => {
            if (item.listItemType === ListItemType.GROUP) {
                const messageId = getGroupMessageId(item);
                return (
                    <SecurityAuditGroupItem
                        name={<FormattedMessage id={messageId as MessageKey} />}
                        count={item.count}
                        isFirstGroup={isFirstGroup}
                    />
                );
            }

            const canEdit = hasWritePermission(item.userPermissions);

            if (isSecurityAuditAgeSecret(item)) {
                return (
                    <SecurityAuditSecretItem
                        id={item.id}
                        name={item.name}
                        canEdit={canEdit}
                        updateSecretRoute={config.routes.securityAuditUpdateSecret}
                        isLastInGroup={isLastInGroup}
                        ignoreable
                    />
                );
            } else if (isSecurityAuditLeaksSecret(item)) {
                return (
                    <SecurityAuditSecretItem
                        id={item.id}
                        name={item.name}
                        canEdit={canEdit}
                        icon={IconType.STATUS_WARNING}
                        isLastInGroup={isLastInGroup}
                        updateSecretRoute={config.routes.securityAuditUpdateSecret}
                        ignoreable
                    />
                );
            } else if (isSecurityAuditDuplicitiesSecret(item)) {
                return (
                    <SecurityAuditSecretItem
                        id={item.id}
                        name={item.name}
                        canEdit={canEdit}
                        updateSecretRoute={config.routes.securityAuditUpdateSecret}
                        isLastInGroup={isLastInGroup}
                        additional={<FormattedMessage id='securityAudit.duplicities.count' values={{ count: item.duplicities }} />}
                        ignoreable
                    />
                );
            }

            return (
                <SecurityAuditSecretItem
                    id={item.id}
                    name={item.name}
                    canEdit={canEdit}
                    icon={item.securityLevel !== undefined ? iconTypeBySecurityLevel[item.securityLevel] : undefined}
                    isLastInGroup={isLastInGroup}
                    updateSecretRoute={config.routes.securityAuditUpdateSecret}
                    ignoreable
                />
            );
        }}
    </SecurityAuditItemRenderer>
);
